import Spacer from "@src/lib/ui/Spacer";
import { Button } from "@src/lib/ui/Button";
import Text from "@src/lib/ui/Text";
import { Input } from "@src/lib/ui/Input";
import { TranslationSetFragment } from "@src/fragments.generated";
import useOrderedLanguageChoices from "@src/features/contentDetail/hooks/useOrderedLanguageChoices";
import {
  removeLangFromTranslationSetOverrides,
  updateTranslationSet,
  useTranslation,
} from "@src/features/contentDetail/utils/translationSets";
import { FC, useState } from "react";
import { cn } from "@src/lib/utils/cn";
import ArrowRightIcon from "@src/lib/icons/18px/arrow-right";
import ChevronDownIcon from "@src/lib/icons/18px/chevron-down";
import PenOutlineIcon from "@src/lib/icons/18px/pen-outline";

type Props = {
  value: TranslationSetFragment;
  onChange: (newValue: TranslationSetFragment) => void;
};

const TranslationsDropdown: FC<Props> = ({ value, onChange }) => {
  const langChoices = useOrderedLanguageChoices();
  const langChoicesWithoutEng = langChoices.filter(
    (lang) => lang.value !== "en",
  );
  const [selectedLanguage, setSelectedLanguage] = useState<Language>("es");
  const googleTranslation = useTranslation(value, selectedLanguage, true).text;
  const existingOverride = value.translationOverrides.find(
    (over) => over.language === selectedLanguage,
  );
  const existingOverrideText = existingOverride?.text;
  return (
    <div className="flex">
      <div className="flex flex-col">
        <Text variant="compact-title" className="mt-0.5">
          English
        </Text>
        <Text
          variant="compact-description"
          className="mt-6 w-[125px] text-gray-12"
          multiline={true}
        >
          {value.en}
        </Text>
      </div>
      <div className="px-3 pr-4">
        <ArrowRightIcon className="text-blue-9" />
      </div>
      <div className="flex flex-col">
        {langChoicesWithoutEng.length !== 0 && (
          <div className="relative flex items-center justify-center">
            <select
              className="relative flex h-8 cursor-pointer appearance-none rounded-lg border border-white px-3 pr-8 pb-2 text-sm font-semibold outline-none"
              value={selectedLanguage}
              onChange={(e) => setSelectedLanguage(e.target.value as Language)}
              onBlur={(e) => setSelectedLanguage(e.target.value as Language)}
            >
              {langChoicesWithoutEng.map((language, i) => (
                <option key={`${language}-${i}`} value={language.value}>
                  {language.label}
                </option>
              ))}
            </select>
            <div className="pointer-events-none absolute top-1/2 right-1 -translate-y-1/2 pb-2">
              <ChevronDownIcon className="size-4" />
            </div>
          </div>
        )}
        <Spacer size={8} />
        <div
          className={cn(
            "mt-2 flex w-[183px] flex-col rounded-lg border p-1.5",
            existingOverride
              ? "border-blue-3 bg-transparent"
              : "border-gray-4 bg-gray-4",
          )}
        >
          <Text
            variant="compact-title"
            multiline={true}
            className={cn(
              "text-gray-8",
              "self-stretch border-b border-gray-3 px-2 pb-0.5",
              existingOverride && "line-through",
            )}
          >
            {googleTranslation}
            <Spacer size={8} />
          </Text>
          {!existingOverride && (
            <div
              onClick={() =>
                onChange(
                  updateTranslationSet({
                    ts: value,
                    lang: selectedLanguage,
                    text: "",
                  }),
                )
              }
              className="mt-3 cursor-pointer border-0 bg-gray-4 pb-1.5 pl-3"
            >
              <PenOutlineIcon className="mr-[3px] text-gray-8" />
              <Text variant="compact-title">Override</Text>
            </div>
          )}
          {existingOverride && (
            <>
              <Spacer size={3} />
              <Input
                value={existingOverrideText || ""}
                placeholder="Type here..."
                onChange={(e) =>
                  onChange(
                    updateTranslationSet({
                      ts: value,
                      lang: selectedLanguage,
                      text: e.target.value,
                    }),
                  )
                }
                inputClassName="z-[1] mt-2 rounded-none border-transparent shadow-none focus-visible:ring-0"
              />

              <div className="ml-auto">
                <Button
                  variant="ghost"
                  onClick={() => {
                    const newTransSet = removeLangFromTranslationSetOverrides(
                      value,
                      selectedLanguage,
                    );
                    onChange(newTransSet);
                  }}
                >
                  Remove
                </Button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default TranslationsDropdown;
